<template>
    <div class="tw:flex tw:flex-col tw:gap-6 tw:p-6">
        <div class="tw:flex tw:items-center tw:justify-between">
            <div>
                <h2 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide">
                    Documentación
                </h2>
                <p class="tw:text-xs tw:font-semibold tw:text-gray-400">
                    Aqui puedes subir la documentación
                </p>
            </div>
        </div>
        <!-- Dropzone reutilizable en modo documentos -->
        <Dropzone
            v-if="url"
            :url="url"
            :accepted-files="acceptFormats"
            :documents="documentos"
            :uploading="uploading"
            :uploading-name="uploadingName"
            :upload-error="uploadError"
            @file-selected="onFileSelected"
            @edit-document="doc => $emit('edit', doc)"
            @delete-document="doc => $emit('delete', doc.id)"
            @view-document="onViewDocument"
            @download-document="onDownloadDocument"
        />

        <ViewDocumento
            v-if="viewingDoc"
            :documento="viewingDoc"
            :loading="downloading"
            @close="closeViewDocument"
            @download="onDownloadDocument"
        />

        <BorrarModalComponent
            v-if="selectedDoc"
            :name="'borrar-doc-' + selectedDoc.id"
            :title="'Borrar documento ' + selectedDoc.nombre"
            @close="$emit('close-delete-modal')"
            @confirm="$emit('confirm-delete', selectedDoc.id)"
        >
            ¿Está segur@ de borrar este documento?
        </BorrarModalComponent>

        <EditDocumento
            v-if="selectedDoc"
            :documento="selectedDoc"
            @close="$emit('close-edit-modal')"
            @confirm="$emit('confirm-edit', $event)"
        />
    </div>
</template>

<script setup>
    import { ref, getCurrentInstance, nextTick } from 'vue'
    import Dropzone from '@/modules/admin/ui/Dropzone.vue'
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue'
    import EditDocumento from '@/modules/admin/abonados/components/EditDocumento.vue'
    import ViewDocumento from '@/modules/admin/documentos/components/ViewDocumento.vue'
    import { useToast } from '@/composables/common/useToast'
    import { useAbonadoDocumentDownload } from '@/composables/admin/abonados/useAbonadoDocumentDownload'

    const props = defineProps({
        documentos: {
            type: Array,
            default: () => []
        },
        url: {
            type: String,
            required: true
        },
        abonadoId: {
            type: [Number, String],
            required: true
        },
        selectedDoc: {
            type: Object,
            default: null
        }
    })

    const emit = defineEmits([
        'edit',
        'delete',
        'upload-success',
        'close-delete-modal',
        'confirm-delete',
        'close-edit-modal',
        'confirm-edit',
    ])

    const toast = useToast()
    const { download, loading: downloading } = useAbonadoDocumentDownload()
    const instance = getCurrentInstance()

    const acceptFormats = 'image/jpeg,image/png,image/gif,image/bmp,image/tiff,application/pdf,.pdf'
    const uploading = ref(false)
    const uploadingName = ref('')
    const uploadError = ref('')
    const viewingDoc = ref(null)

    function onViewDocument(doc) {
        viewingDoc.value = doc
        nextTick(() => {
            instance.proxy.$modal.show(`view-documento-${doc.id}`)
        })
    }

    function closeViewDocument() {
        viewingDoc.value = null
    }

    function onDownloadDocument(doc) {
        if (!doc) return
        download(doc, props.abonadoId)
    }

    function onFileSelected(file) {
        uploadError.value = ''
        uploadingName.value = file.name

        // Validate type
        const allowed = acceptFormats.split(',').map(s => s.trim())
        const ext = '.' + file.name.split('.').pop()?.toLowerCase()
        const mimeOk = allowed.includes(file.type)
        const extOk = allowed.includes(ext)
        if (!mimeOk && !extOk) {
            uploadError.value = 'Formato no soportado. Usa: PDF, JPG, PNG, GIF, BMP, TIFF'
            return
        }

        // Validate size (10 MB)
        if (file.size > 10 * 1024 * 1024) {
            uploadError.value = 'El archivo es demasiado grande. Máximo 10 MB.'
            return
        }

        uploading.value = true

        const formData = new FormData()
        formData.append('file', file)

        const xhr = new XMLHttpRequest()
        xhr.open('POST', props.url)
        xhr.setRequestHeader('Accept', 'application/json')

        const hashTeatro = localStorage.getItem('hash_teatro')
        if (hashTeatro) xhr.setRequestHeader('Hash-Teatro', hashTeatro)

        xhr.onload = () => {
            uploading.value = false
            if (xhr.status >= 200 && xhr.status < 300) {
                toast.success('Documento subido correctamente')
                emit('upload-success')
            } else {
                uploadError.value = 'Error del servidor al subir el documento. Inténtalo de nuevo.'
            }
        }

        xhr.onerror = () => {
            uploading.value = false
            uploadError.value = 'Error de conexión al subir el documento.'
        }

        xhr.ontimeout = () => {
            uploading.value = false
            uploadError.value = 'La subida tardó demasiado. Inténtalo de nuevo.'
        }

        xhr.send(formData)
    }
</script>
